import { Call, Fn } from "hotscript";

import Chainable from "./Chainable.mts";
import Symbols from "./Symbols.mts";
import UArray from "./UArray.mts";
import UFunction from "./UFunction.mts";
import UPromise from "./UPromise.mts";
import UProxy from "./UProxy.mts";
import UTuple from "./UTuple.mts";
import UType from "./UType.mts";

declare global {
  interface ObjectConstructor {
    // Using UType.UnionToIntersection<T[number]> will resolve to unknown when
    // passing arguments with generic types, use UTuple.IntersectItems instead
    assign<T extends [any, ...any[]]>(...args: T): UTuple.IntersectItems<T>;

    keys<Obj extends UObject.Any>(obj: Obj): `${Exclude<keyof Obj, symbol>}`[];
    values<Obj extends UObject.Any>(obj: Obj): UObject.Value<Obj>[];
    entries<Obj extends UObject.Any>(obj: Obj): UObject.Entry.FromNative<Obj>[];
    fromEntries<const Entry extends readonly [key: UObject.Key, value?: any]>(
      entries: Iterable<Entry>
    ): UObject.FromEntryUnion<Entry>;
    groupBy<K extends PropertyKey, T>(
      items: Iterable<T>,
      keySelector: (item: T, index: number) => K
    ): string extends K ? Record<K, T[]> : Partial<Record<K, T[]>>;
  }
}

namespace UObject {
  export type Any =
    | Record<Key, any>
    | Record<string, any>
    | Record<number, any>
    | Record<symbol, any>;
  export type All = Record<Key, any>;
  export type Empty = Record<Key, never>;
  export type Key = PropertyKey;

  export namespace Key {
    export type OfUnion<T> = T extends T ? keyof T : never;

    export type FilterByEntry<
      Obj extends Any,
      MatchEntry extends [Key, any],
    > = Value<{
      [Key in keyof Obj]: [Key, Obj[Key]] extends MatchEntry ? Key : never;
    }>;

    export type ForValue<
      Obj extends Any,
      Value extends UObject.Value<Obj>,
    > = UObject.Value<{
      [Key in keyof Obj]: Obj[Key] extends Value ? Key : never;
    }>;
  }
  export type Value<Obj extends Any> = Obj[keyof Obj];
  export namespace Value {
    export type OfUnion<
      Obj extends Any,
      Key extends Key.OfUnion<Obj> = Key.OfUnion<Obj>,
    > = Value<{
      [K in Key]: Extract<Obj, { [_ in K]: any }>[K];
    }>;
  }

  export type Entry<Obj extends Any> = Value<{
    [Key in Key.OfUnion<Obj>]: [Key, Extract<Obj, Any>[Key]];
  }>;
  export namespace Entry {
    export type Any = readonly [Key, any];
    // From native Object.entries
    export type FromNative<Obj extends UObject.Any> = Value<{
      [Key in Key.OfUnion<Obj>]: Key extends symbol
        ? never
        : [`${Exclude<Key, symbol>}`, Extract<Obj, UObject.Any>[Key]];
    }>;
  }

  export type GetFromUnion<
    Obj extends Any,
    Key extends Key.OfUnion<Obj>,
  > = Obj extends { [_ in Key]: infer T } ? T : never;

  // For when you need an object instead of an interface, usually to fulfill
  // index signature
  export type Resolve<Obj extends Any> = {
    [Key in keyof Obj]: Obj[Key];
  };

  export namespace Pick {
    export type FromUnion<
      Obj extends Any,
      K extends Key.OfUnion<Obj> | (Key & {}),
    > = Obj extends Obj
      ? {
          [Key in K as Key extends keyof Obj ? Key : never]: Obj[UType.Narrow<
            Key,
            keyof Obj
          >];
        }
      : never;
  }
  export const pick = <Obj extends Any, Key extends UObject.Key.OfUnion<Obj>>(
    obj: Obj,
    keys: Key[]
  ): Pick.FromUnion<Obj, Key> => {
    const keySet = new Set(keys);
    return transform(obj, (kv) =>
      keySet.has(kv[0] as Key) ? kv : undefined
    ) as any;
  };

  export type PartialBy<
    Obj extends Any,
    K extends Key.OfUnion<Obj> | (Key & {}),
  > = Omit<Obj, K> & Partial<Pick.FromUnion<Obj, K>>;
  export type PartialExcept<
    Obj extends Any,
    K extends Key.OfUnion<Obj> | (Key & {}),
  > = Pick.FromUnion<Obj, K> & Partial<Omit<Obj, K>>;

  export type DeepPartial<T> = {
    [P in keyof T]?: keyof T[P] extends never ? T[P] : DeepPartial<T[P]>;
  };
  export type DeepPartialBy<
    T,
    K extends string | number | symbol,
  > = T extends object
    ? T extends any[]
      ? DeepPartialBy<T[number], K>[]
      : {
          [P in Exclude<keyof T, K>]: DeepPartialBy<T[P], K>;
        } & {
          [P in K & keyof T]?: DeepPartialBy<T[P], K>;
        }
    : T;

  export type DeepOmit<T, K extends string | number | symbol> = T extends object
    ? T extends any[]
      ? DeepOmit<T[number], K>[]
      : {
          [P in Exclude<keyof T, K>]: DeepOmit<T[P], K>;
        }
    : T;

  export type OmitPath<
    T,
    Path extends readonly [PropertyKey, ...PropertyKey[]],
  > = T extends object
    ? Path extends readonly [
        infer Key extends PropertyKey,
        ...infer Rest extends readonly PropertyKey[],
      ]
      ? Rest extends readonly [PropertyKey, ...PropertyKey[]]
        ? Key extends keyof T
          ? Omit<T, Key> & { [P in Key]: OmitPath<T[Key], Rest> }
          : T
        : Omit<T, Key>
      : never
    : T;

  // Disallows undefined fields
  export type StrictRequired<T> = {
    [K in keyof T]-?: Exclude<T[K], undefined>;
  };

  export type RequiredBy<
    Obj extends Any,
    K extends Key.OfUnion<Obj> | (string & {}),
  > = Omit<Obj, K> & StrictRequired<Pick.FromUnion<Obj, K>>;
  export type RequiredExcept<
    Obj extends Any,
    K extends Key.OfUnion<Obj> | (string & {}),
  > = Pick.FromUnion<Obj, K> & StrictRequired<Omit<Obj, K>>;

  export type RequiredKeys<T extends Any> = {
    [K in keyof T]-?: {} extends Pick<T, K> ? never : K;
  }[keyof T];

  export type FromEntryUnion<T extends readonly [key: Key, value?: any]> = {
    [Key in T[0]]: Extract<T, readonly [Key, any]>[1] extends never
      ? T[1]
      : Extract<T, readonly [Key, any]>[1];
  };

  export type ExpandUnion<T extends Any> = T extends any
    ? Value<{
        [Key in keyof T]: UTuple.FromUnion<T[Key]> extends infer UnionTuple
          ? Value<{
              [I in Exclude<keyof UnionTuple, keyof []>]: Override<
                T,
                UType.Narrow<{ [_ in Key]: UnionTuple[I] }, Partial<T>>
              >;
            }>
          : never;
      }>
    : never;

  export type Path<T extends Any, Prefix extends Key[] = []> =
    // TODO: Improve true type performance, use flexible type until then
    any extends any
      ? UObject.Key[]
      : UType.Default<
          Value<{
            [Key in keyof T]: T[Key] extends Any
              ? T[Key] extends T
                ? UObject.Key[]
                : [...Prefix, Key] | Path<T[Key], [...Prefix, Key]>
              : [...Prefix, Key];
          }>,
          Key[]
        >;
  export type Override<T extends Any, Overrides> = Omit<T, keyof Overrides> &
    Overrides;

  export const from = {
    keys<const Key extends UObject.Key, T>(keys: readonly Key[], value: T) {
      return Object.fromEntries(keys.map((key) => [key, value])) as Record<
        Key,
        T
      >;
    },
  };

  export const is = (maybeObj: any): maybeObj is Any =>
    maybeObj != null &&
    (maybeObj[Symbols.typeOf] || typeof maybeObj) === "object";

  export function isPlain(v: unknown): v is UObject.Any {
    if (!is(v)) return false;
    const proto = Object.getPrototypeOf(v);
    return proto === Object.prototype || proto === null;
  }

  export const entries = <Obj extends Any>(obj: Obj) =>
    Object.entries(obj) as Entry.FromNative<Obj>[];

  // Use if `in` keyword doesn't narrow type properly
  export function hasKey<Obj extends Any, Key extends UObject.Key.OfUnion<Obj>>(
    obj: Obj,
    key: Key
  ): obj is Obj & { [_ in Key]: any };
  export function hasKey<Obj extends Any, Key extends UObject.Key>(
    obj: Obj,
    key: Key
  ): key is Key & UObject.Key.OfUnion<Obj>;
  export function hasKey(obj: Any, key: Key) {
    return key in obj;
  }

  export type Select<T, R = any> =
    | ((obj: T) => R)
    | (UType.IsAny<T> extends true
        ? UObject.Key
        : T extends Any
          ? Key.FilterByEntry<T, [UObject.Key, R]>
          : never);
  export declare namespace Select {
    // Using infer with select doesn't work, use function variant from type to
    // resolve these
    type Subject<S extends Select<any>> = S extends UFunction.Any
      ? Parameters<S>[0]
      : never;

    type Result<T, S extends Select<any>> =
      S extends Select<any, infer R>
        ? UType.Narrow<
            S extends UFunction.Any
              ? ReturnType<S>
              : T extends Any
                ? S extends Key.OfUnion<T>
                  ? Value.OfUnion<T, S>
                  : never
                : never,
            R
          >
        : never;
  }
  export function createSelector<S extends Select<any>>(
    select: S
  ): <Obj extends Select.Subject<S>>(obj: Obj) => Select.Result<Obj, S> {
    if (typeof select !== "function") {
      const key = select;
      // @ts-expect-error Ignore generic constraint
      select = (obj) => obj[key];
    }

    return select as any;
  }

  export function transform<
    Obj extends UObject.Any,
    Transform extends (
      entry: Entry.FromNative<Obj> extends never
        ? [never, never]
        : Entry.FromNative<Obj>
    ) => Entry.Any | false | null | undefined,
  >(obj: Obj, transform: Transform) {
    // TODO: Benchmark against building object with loop
    return Object.fromEntries(
      Object.entries<Value<Obj>>(obj).flatMap((entry) => {
        const res = transform(entry as any);
        return res ? [res] : [];
      }) as any
      // We're checking TypeFn for transform twice because: never will extend Fn,
      // need to check for never first before extracting TypeFn
    ) as UFunction.TypeFn<Transform> extends never
      ? UObject.FromEntryUnion<UType.Narrow<ReturnType<Transform>, Entry.Any>>
      : UFunction.TypeFn<Transform> extends infer TypeFn extends Fn
        ? {
            [Key in keyof Obj as UType.Narrow<
              Call<TypeFn, [Key, Obj[Key]]>,
              [UObject.Key, any]
            >[0]]: UType.Narrow<
              Call<TypeFn, [Key, Obj[Key]]>,
              [UObject.Key, any]
            >[1];
          }
        : never;
  }
  transform.deep = <Obj extends Any, Result extends Entry.Any>(
    obj: Obj,
    transform: (
      entry: Entry.FromNative<Obj>,
      next: () => any,
      obj: Obj
    ) => Result | false | null | undefined
  ) =>
    (function next(obj: any): any {
      if (!obj || typeof obj !== "object") return obj;
      if (Array.isArray(obj)) return obj.map(next);
      return UObject.transform(obj, ([key, value]) =>
        transform([key, value] as Entry.FromNative<Obj>, () => next(value), obj)
      );
    })(obj);

  export async function transformAsync<
    Obj extends UObject.Any,
    Result extends Entry.Any,
  >(
    obj: Obj,
    transform: (
      entry: Entry.FromNative<Obj>
    ) => UPromise.Awaitable<Result | false | null | undefined>
  ) {
    return Object.fromEntries(
      (
        await Promise.all(
          Object.entries<Value<Obj>>(obj).map(async (entry) => {
            const res = await transform(entry as any);
            return res ? [res] : [];
          }) as any
        )
      ).flat()
    ) as Promise<UObject.FromEntryUnion<Result>>;
  }

  export type TrimKeys<
    Obj extends Record<`${Prefix}${string}${Suffix}`, any>,
    Prefix extends string,
    Suffix extends string = "",
  > = string extends keyof Obj
    ? Obj
    : {
        [Key in keyof Obj as Key extends `${Prefix}${infer T}${Suffix}`
          ? T
          : never]: Obj[Key];
      };
  export const trimKeys = <
    Obj extends Record<`${Prefix}${string}${Suffix}`, any>,
    Prefix extends string,
    Suffix extends string = "",
  >(
    obj: Obj,
    prefix: Prefix,
    suffix = "" as Suffix
  ): TrimKeys<Obj, Prefix, Suffix> =>
    Object.fromEntries(
      Object.entries(obj).map(([k, v]) => {
        if (!k.startsWith(prefix))
          throw new Error(
            `Found key "${k}" that does not start with prefix "${prefix}"`
          );
        if (!k.endsWith(suffix))
          throw new Error(
            `Found key "${k}" that does not start with suffix "${suffix}"`
          );

        return [k.slice(prefix.length).slice(0, -suffix.length || Infinity), v];
      })
    ) as any;

  export const inverse = <const Obj extends Record<Key, Key>>(obj: Obj) =>
    transform(obj, ([key, value]: any) => [value, key]) as {
      [Key in keyof Obj as Obj[Key]]: Key;
    };

  export const inverseMap = <const Obj extends Record<Key, any>>(
    obj: Obj
  ): Map<Value<Obj>, keyof Obj> =>
    new Map(Object.entries(obj).map(([key, value]) => [value, key]));

  export const omit = <Obj extends UObject.Any, Props extends (keyof Obj)[]>(
    obj: Obj,
    props: Props
  ): Omit<Obj, Props[number]> =>
    transform(
      obj,
      (
        // TODO: Fix transform type
        [key, value]: any
      ) => !props.includes(key) && [key, value]
    ) as any;

  export const omitSymbolKeys = <Obj extends UObject.Any>(
    obj: Obj
  ): Omit<Obj, symbol> =>
    transform(
      obj,
      ([key, value]: any) => typeof key !== "symbol" && [key, value]
    ) as any;

  export function matchesPartial<
    Obj extends UObject.Any,
    Partial extends UObject.Any,
  >(obj: Obj, partial: Partial): obj is Extract<Obj, Partial> {
    return Object.entries(partial).every(
      ([key, value]) => value === (obj as any)[key]
    );
  }
  matchesPartial.deep = <Obj extends UObject.Any, Partial extends UObject.Any>(
    obj: Obj,
    partial: Partial
  ): obj is Extract<Obj, Partial> => {
    return Object.entries(partial).every(([key, value]): boolean => {
      // @ts-expect-error
      const objField = obj[key];
      if (value == null || typeof value !== "object") return value === objField;
      if (Array.isArray(value))
        return UArray.set.intersection(value, objField).length > 0;

      return matchesPartial.deep(objField, value);
    });
  };

  export const getFromUnion = <Obj extends Any, Key extends Key.OfUnion<Obj>>(
    obj: Obj,
    key: Key
  ) => obj[key] as Value.OfUnion<Obj, Key>;

  export const getInstanceOf = <
    Constructor extends new (...args: any[]) => any,
  >(
    maybeInstance: unknown,
    constructor: Constructor
  ): InstanceType<Constructor> | undefined =>
    maybeInstance instanceof constructor ? maybeInstance : undefined;

  // Cannot directly assign, causes moodule loading issues
  export const merge = ((...args: Parameters<typeof UProxy.merge>) =>
    UProxy.merge(...args)) as typeof UProxy.merge;

  export function clone<Obj extends UObject.Any>(obj: Obj): Obj {
    const circular = new Map<object, unknown>();
    return (function next(value: unknown): unknown {
      if (!is(value)) return value;
      if (circular.has(value as object)) return circular.get(value as object);
      const instance = (() => {
        if (value instanceof Date) return new Date(value);
        if (value instanceof RegExp)
          return new RegExp(value.source, value.flags);
        if (value instanceof Map) return new Map(value);
        if (value instanceof Set) return new Set(value);
        if (value instanceof Error) return new Error(value.message);
        if (value instanceof File)
          return new File([value], value.name, { type: value.type });
        if (value instanceof Blob)
          return new Blob([value], { type: value.type });
        // Handles Arrays
        if (typeof (value as any).constructor?.from === "function")
          return (value as any).constructor.from(value);
        if (
          typeof (value as any).constructor?.parse === "function" &&
          typeof (value as any).toString === "function"
        )
          return (value as any).constructor.parse(value.toString());
      })();

      // e.g. Object.assign([...], { someFlag: true })
      const withMonkeyPatches = Object.assign(
        instance || {},
        transform(value, ([k, v]) => [
          k,
          !instance?.[k] || v === instance?.[k] ? next(v) : v,
        ])
      );
      circular.set(value as object, withMonkeyPatches);

      return withMonkeyPatches;
    })(obj) as Obj;
  }

  export function groupByEntry<T, R>(
    iterable: Iterable<T>,
    fn: (item: T) => [Key, R]
  ) {
    const groups: Record<Key, R[]> = {};
    for (const item of iterable) {
      const [key, value] = fn(item);
      (groups[key] ??= []).push(value);
    }
    return groups;
  }

  export const groupByMultiple = <T, Key extends UObject.Key>(
    iterable: Iterable<T>,
    keySelector: (item: T, index: number) => readonly Key[]
  ) => {
    const groups: Partial<Record<Key, T[]>> = {};
    let i = 0;
    for (const item of iterable) {
      for (const key of keySelector(item, i)) {
        (groups[key] ??= []).push(item);
      }
      i++;
    }
    return groups;
  };

  export const groupByMultipleEntries = <T, Key extends UObject.Key, R>(
    iterable: Iterable<T>,
    keySelector: (
      item: T,
      index: number
    ) => readonly (readonly [Key | readonly Key[], R])[]
  ) => {
    const groups: Partial<Record<Key, R[]>> = {};
    let i = 0;
    for (const item of iterable) {
      for (const [keys, value] of keySelector(item, i)) {
        for (const key of UArray.ensure(keys)) {
          (groups[key as Key] ??= []).push(value);
        }
      }
      i++;
    }
    return groups;
  };

  export const deepKey = (() => {
    const deepKey = (keyTransform: (key: string) => Key) => {
      const walk = (v: unknown) =>
        v && typeof v === "object" ? deepKey(keyTransform)(v) : v;

      return (obj: UObject.Any): UObject.Any =>
        obj &&
        ((Array.isArray(obj)
          ? obj.map(walk)
          : transform(obj, ([k, v]) => [
              keyTransform(k as string),
              walk(v),
            ])) as UObject.Any);
    };

    return Object.assign(deepKey, {
      toCamel: deepKey((k) => k.replace(/_[a-z]/g, (s) => s[1]!.toUpperCase())),
      toSnake: deepKey((k) =>
        k.replace(/[A-Z]/g, (s) => `_${s.toLowerCase()}`)
      ),
    });
  })();

  export type GetByPath<
    Obj extends UObject.Any,
    Path extends UObject.Path<Obj>,
  > = Path extends [
    infer NextKey extends Key,
    ...infer Rest extends UObject.Key[],
  ]
    ? Obj extends Record<NextKey, infer T>
      ? Rest extends []
        ? T
        : T extends UObject.Any
          ? GetByPath<T, Rest>
          : never
      : never
    : never;

  export const getByPath = <
    Obj extends UObject.Any,
    Path extends UObject.Path<Obj>,
  >(
    obj: Obj,
    path: Path
  ) =>
    (function next(current: any, i = 0): any {
      if (i >= path.length) return current;
      if (current == null)
        throw new Error(
          `Cannot read property of undefined at path \`${path.slice(0, i).join(".")}\` (reading "${String(path[i])}")`
        );
      return next(current[(path as any)[i]], i + 1);
    })(obj);

  export const mut = {
    setByPath: Chainable.create(
      Chainable.wrapFactory(
        ({ createParents = false }) =>
          <Obj extends UObject.Any>(
            obj: Obj,
            path: UObject.Path<Obj>,
            value: any
          ): void =>
            (function next(current: any, i = 0) {
              if (!current)
                throw new Error(
                  "Field in path is undefined, if you need to create parents as needed, use setByPath.createParents."
                );
              const property = (path as any)[i] as any;
              if (i === path.length - 1) {
                current[property] = value;
              } else {
                if (createParents) current[property] ??= {};
                next(current[property], i + 1);
              }
            })(obj)
      ),
      { createParents: { createParents: true } }
    ),
  };
}

export default UObject;
